import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import StripeCheckoutButton from '@/components/StripeCheckoutButton';
import BuyCreditsModal from '@/components/BuyCreditsModal';
import { 
  User, 
  CreditCard, 
  Coins, 
  Check, 
  Loader2, 
  AlertTriangle,
  Share2,
  Eye,
  MousePointer,
  TrendingUp,
  ExternalLink,
  Pencil,
  X,
  Save,
  Lock,
  Shield,
  Star,
  CheckCircle,
  XCircle,
  HelpCircle,
  Plus,
  Zap
} from 'lucide-react';

// Social platform icons
const TwitterIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const LinkedinIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const SettingsPage = () => {
  const { user, api, refreshUser, logout } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [shareAnalytics, setShareAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Admin state
  const [pendingTestimonials, setPendingTestimonials] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  
  // Billing portal state
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  
  // Buy credits modal state
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  
  // Purchase history state
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [purchaseHistoryLoading, setPurchaseHistoryLoading] = useState(false);

  const plans = [
    { id: 'starter', name: 'Starter', price: 15, credits: 100 },
    { id: 'growth', name: 'Growth', price: 29, credits: 500, popular: true },
    { id: 'pro', name: 'Pro', price: 49, credits: 2000 },
  ];

  useEffect(() => {
    if (user) {
      setProfileForm(prev => ({ ...prev, name: user.name || '' }));
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
    if (location.state?.selectedPlan) {
      setActiveTab('billing');
    }
    
    // Handle Stripe checkout return
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const creditsPurchase = params.get('credits_purchase');
    const sessionId = params.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      // Check the checkout status for subscription
      checkStripeCheckoutStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled. You can try again anytime.');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Handle credit purchase return
    if (creditsPurchase === 'success' && sessionId) {
      checkCreditPurchaseStatus(sessionId);
    } else if (creditsPurchase === 'cancelled') {
      toast.info('Credit purchase was cancelled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location]);

  const checkStripeCheckoutStatus = async (sessionId) => {
    try {
      const response = await api.get(`/subscriptions/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        // Refresh user data
        fetchSubscription();
        // Refresh the page to get updated user info
        window.location.href = '/dashboard/settings?tab=billing';
      } else if (response.data.status === 'expired') {
        toast.error('Checkout session expired. Please try again.');
      }
      
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } catch (error) {
      console.error('Failed to check checkout status:', error);
      toast.error('Could not verify payment status. Please check your subscription.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const checkCreditPurchaseStatus = async (sessionId) => {
    try {
      const response = await api.get(`/credits/purchase-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid' || response.data.status === 'completed') {
        const creditsAdded = response.data.credits_added || response.data.credits;
        toast.success(`Payment successful! ${creditsAdded} credits added to your account.`);
        // Refresh user data
        await refreshUser();
        setActiveTab('credits');
      } else if (response.data.status === 'expired') {
        toast.error('Checkout session expired. Please try again.');
      }
      
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } catch (error) {
      console.error('Failed to check credit purchase status:', error);
      toast.error('Could not verify payment. Please check your credit balance.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchShareAnalytics();
    }
    if (activeTab === 'admin') {
      fetchPendingTestimonials();
    }
    if (activeTab === 'credits') {
      fetchPurchaseHistory();
    }
  }, [activeTab]);

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/subscriptions/current');
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const fetchShareAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await api.get('/share/analytics/summary');
      setShareAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchPendingTestimonials = async () => {
    setAdminLoading(true);
    try {
      const response = await api.get('/testimonials/pending');
      setPendingTestimonials(response.data.testimonials || []);
    } catch (error) {
      console.error('Failed to fetch pending testimonials:', error);
    } finally {
      setAdminLoading(false);
    }
  };

  const fetchPurchaseHistory = async () => {
    setPurchaseHistoryLoading(true);
    try {
      const response = await api.get('/credits/purchase-history');
      setPurchaseHistory(response.data.purchases || []);
    } catch (error) {
      console.error('Failed to fetch purchase history:', error);
    } finally {
      setPurchaseHistoryLoading(false);
    }
  };

  const handleApproveTestimonial = async (testimonialId, approved) => {
    setProcessingId(testimonialId);
    try {
      await api.post('/testimonials/approve', {
        testimonial_id: testimonialId,
        approved
      });
      toast.success(approved ? 'Testimonial approved!' : 'Testimonial rejected');
      setPendingTestimonials(prev => prev.filter(t => t.id !== testimonialId));
    } catch (error) {
      toast.error('Failed to process testimonial');
    } finally {
      setProcessingId(null);
    }
  };

  const handleSubscribe = (plan) => {
    setSelectedPlan(plan);
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    setSelectedPlan(null);
    fetchSubscription();
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
    setSelectedPlan(null);
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    try {
      await api.post('/subscriptions/cancel');
      toast.success('Subscription canceled successfully');
      await refreshUser();
      await fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  // Profile update handlers
  const handleProfileUpdate = async () => {
    // Validation
    if (profileForm.newPassword && profileForm.newPassword !== profileForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (profileForm.newPassword && profileForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (profileForm.newPassword && !profileForm.currentPassword) {
      toast.error('Current password is required to change password');
      return;
    }

    setProfileLoading(true);
    try {
      const updateData = {};
      
      if (profileForm.name && profileForm.name !== user?.name) {
        updateData.name = profileForm.name;
      }
      
      if (profileForm.newPassword) {
        updateData.current_password = profileForm.currentPassword;
        updateData.new_password = profileForm.newPassword;
      }
      
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        setIsEditingProfile(false);
        return;
      }

      await api.put('/users/profile', updateData);
      toast.success('Profile updated successfully');
      await refreshUser();
      setIsEditingProfile(false);
      setProfileForm(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const cancelProfileEdit = () => {
    setIsEditingProfile(false);
    setProfileForm({
      name: user?.name || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleManageBilling = async () => {
    // For free users without payment history
    if (user?.plan === 'free') {
      toast.info('Subscribe to a plan to access billing management');
      return;
    }
    
    setBillingPortalLoading(true);
    try {
      const returnUrl = window.location.href;
      const response = await api.post('/billing/portal', { return_url: returnUrl });
      
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Unable to open billing portal');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to access billing portal';
      toast.error(message);
    } finally {
      setBillingPortalLoading(false);
    }
  };

  // Define base tabs - Admin tab only visible to admin users
  const baseTabs = [
    { id: 'account', label: 'Account', icon: <User className="w-4 h-4" /> },
    { id: 'billing', label: 'Billing & Plans', icon: <CreditCard className="w-4 h-4" /> },
    { id: 'credits', label: 'Credits', icon: <Coins className="w-4 h-4" /> },
    { id: 'analytics', label: 'Share Analytics', icon: <Share2 className="w-4 h-4" /> },
  ];
  
  // Only add Admin tab if user is an admin
  const isAdmin = user?.role === 'admin' || user?.is_admin === true;
  const tabs = isAdmin 
    ? [...baseTabs, { id: 'admin', label: 'Admin', icon: <Shield className="w-4 h-4" /> }]
    : baseTabs;

  const platformIcons = {
    twitter: <TwitterIcon />,
    facebook: <FacebookIcon />,
    pinterest: <PinterestIcon />,
    linkedin: <LinkedinIcon />,
  };

  const platformColors = {
    twitter: 'bg-[#1DA1F2]',
    facebook: 'bg-[#4267B2]',
    pinterest: 'bg-[#E60023]',
    linkedin: 'bg-[#0A66C2]',
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 fade-in" data-testid="settings-page">
      {/* Stripe Checkout Dialog */}
      <Dialog open={showPaymentForm} onOpenChange={setShowPaymentForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Your Subscription</DialogTitle>
          </DialogHeader>
          {selectedPlan && (
            <StripeCheckoutButton
              planType={selectedPlan.id}
              planName={selectedPlan.name}
              planPrice={selectedPlan.price_display || `$${selectedPlan.price}/mo`}
              onSuccess={handlePaymentSuccess}
              onCancel={handlePaymentCancel}
            />
          )}
        </DialogContent>
      </Dialog>

      <div>
        <h1 
          className="text-2xl font-bold mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Settings
        </h1>
        <p className="text-muted-foreground">
          Manage your account, billing, and preferences
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap
              ${activeTab === tab.id 
                ? 'bg-accent text-foreground' 
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Account Tab */}
      {activeTab === 'account' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Profile Information</CardTitle>
                {!isEditingProfile ? (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setIsEditingProfile(true)}
                    data-testid="edit-profile-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={cancelProfileEdit}
                      disabled={profileLoading}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleProfileUpdate}
                      disabled={profileLoading}
                      data-testid="save-profile-btn"
                    >
                      {profileLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="profile-name">Name</Label>
                  {isEditingProfile ? (
                    <Input 
                      id="profile-name"
                      value={profileForm.name} 
                      onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Your name"
                      data-testid="profile-name-input"
                    />
                  ) : (
                    <Input value={user?.name || ''} disabled data-testid="profile-name" />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-email">Email</Label>
                  <Input 
                    id="profile-email"
                    value={user?.email || ''} 
                    disabled 
                    className="bg-muted"
                    data-testid="profile-email" 
                  />
                  <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                </div>
              </div>
              
              {/* Password Change Section */}
              {isEditingProfile && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="pt-4 border-t space-y-4"
                >
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-muted-foreground" />
                    <Label className="text-sm font-medium">Change Password (optional)</Label>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="current-password">Current Password</Label>
                      <Input 
                        id="current-password"
                        type="password"
                        value={profileForm.currentPassword}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                        placeholder="••••••••"
                        data-testid="current-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-password">New Password</Label>
                      <Input 
                        id="new-password"
                        type="password"
                        value={profileForm.newPassword}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, newPassword: e.target.value }))}
                        placeholder="••••••••"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm New Password</Label>
                      <Input 
                        id="confirm-password"
                        type="password"
                        value={profileForm.confirmPassword}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="••••••••"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Leave password fields empty to keep your current password
                  </p>
                </motion.div>
              )}
              
              {!isEditingProfile && (
                <p className="text-sm text-muted-foreground">
                  Click "Edit Profile" to update your name or change your password.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Account Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Account Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted/30 rounded-xl">
                  <p className="text-2xl font-bold">{user?.credits || 0}</p>
                  <p className="text-xs text-muted-foreground">Credits</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-xl">
                  <p className="text-2xl font-bold capitalize">{user?.plan || 'Free'}</p>
                  <p className="text-xs text-muted-foreground">Current Plan</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-xl">
                  <p className="text-2xl font-bold">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                  <p className="text-xs text-muted-foreground">Member Since</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-xl">
                  <p className="text-2xl font-bold">{shareAnalytics?.total_shares || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Shares</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                Session
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Sign out of your account</p>
                  <p className="text-sm text-muted-foreground">
                    You'll need to sign in again to access your dashboard
                  </p>
                </div>
                <Button variant="outline" onClick={logout} data-testid="logout-btn">
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Current Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold capitalize">{user?.plan || 'Free'}</p>
                  <p className="text-sm text-muted-foreground">
                    {user?.plan === 'free' 
                      ? 'You are on the free trial' 
                      : `${subscription?.plan_details?.credits || 0} credits/month`
                    }
                  </p>
                </div>
                {user?.plan !== 'free' && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="text-destructive" data-testid="cancel-subscription-btn">
                        Cancel Plan
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Your subscription will be canceled immediately. You'll lose access to premium features
                          but keep your remaining credits.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Keep Plan</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleCancelSubscription}
                          className="bg-destructive text-destructive-foreground"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes, Cancel'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </CardContent>
          </Card>

          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {user?.plan === 'free' ? 'Upgrade Your Plan' : 'Change Plan'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((plan) => (
                <Card 
                  key={plan.id}
                  className={`relative ${plan.popular ? 'border-foreground' : ''} 
                    ${user?.plan === plan.id ? 'bg-accent/50' : ''}`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-foreground text-background text-xs font-medium px-2 py-0.5 rounded-full">
                        Popular
                      </span>
                    </div>
                  )}
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-1">{plan.name}</h4>
                    <p className="text-2xl font-bold mb-2">${plan.price}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                    <p className="text-sm text-muted-foreground mb-4">{plan.credits} credits</p>
                    
                    {user?.plan === plan.id ? (
                      <Button disabled className="w-full" variant="outline">
                        <Check className="w-4 h-4 mr-2" /> Current Plan
                      </Button>
                    ) : (
                      <Button 
                        className="w-full" 
                        variant={plan.popular ? "default" : "outline"}
                        onClick={() => handleSubscribe(plan)}
                        data-testid={`subscribe-${plan.id}-btn`}
                      >
                        {user?.plan === 'free' ? 'Subscribe' : 'Switch'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Method</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-12 h-8 bg-muted rounded flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    {user?.plan === 'free' 
                      ? 'No payment method on file' 
                      : 'Card ending in ••••'
                    }
                  </p>
                  <p className="text-xs text-muted-foreground">Secure payments. We never store your card details.</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleManageBilling}
                  disabled={billingPortalLoading}
                  data-testid="manage-billing-btn"
                >
                  {billingPortalLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Manage Billing'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Credits Tab */}
      {activeTab === 'credits' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Credit Balance</CardTitle>
              <Button 
                onClick={() => setShowBuyCreditsModal(true)}
                className="bg-[#4F7CFF] hover:bg-[#4F7CFF]/90"
                data-testid="buy-credits-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Buy Credits
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center">
                  <Coins className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-4xl font-bold" data-testid="credit-balance">{user?.credits || 0}</p>
                  <p className="text-sm text-muted-foreground">credits remaining</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                Credit Usage
                {user?.plan === 'free' && (
                  <span className="ml-2 text-xs font-normal px-2 py-0.5 bg-green-500/10 text-green-600 rounded-full">
                    Trial Pricing
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {user?.plan === 'free' ? (
                  // Trial pricing for free users
                  <>
                    {[
                      { action: 'Image to Prompt', cost: 1 },
                      { action: 'Prompt Enhance', cost: 1 },
                      { action: 'Image Generate', cost: 10 },
                      { action: 'Mockup Generate', cost: 2 },
                      { action: 'Background Generate', cost: 2 },
                    ].map((item) => (
                      <div key={item.action} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <span className="text-sm">{item.action}</span>
                        <span className="text-sm font-medium text-green-600">{item.cost} credit{item.cost > 1 ? 's' : ''}</span>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground mt-3 pt-3 border-t">
                      Trial users get reduced credit costs to explore all features freely.
                    </p>
                  </>
                ) : (
                  // Standard pricing for paid users
                  [
                    { action: 'Image to Prompt', cost: 5 },
                    { action: 'Prompt Enhance', cost: 2 },
                    { action: 'Image Generate', cost: 15 },
                    { action: 'Mockup Generate', cost: 20 },
                    { action: 'Background Generate', cost: 10 },
                  ].map((item) => (
                    <div key={item.action} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <span className="text-sm">{item.action}</span>
                      <span className="text-sm font-medium">{item.cost} credits</span>
                    </div>
                  ))
                )}
              </div>
              <div className="mt-4 pt-4 border-t">
                <Link 
                  to="/how-credits-work" 
                  className="text-sm flex items-center gap-1.5 hover:underline"
                  style={{ color: '#4F7CFF' }}
                >
                  <HelpCircle className="w-4 h-4" />
                  Learn more about how credits work
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Buy Credits Card - Always visible */}
          <Card className="border-[#4F7CFF]/30 bg-[#4F7CFF]/5">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#4F7CFF]/10 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-[#4F7CFF]" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Need more credits?</p>
                <p className="text-sm text-muted-foreground">
                  Buy credit packs instantly. Starting at $20 for 100 credits.
                </p>
              </div>
              <Button 
                onClick={() => setShowBuyCreditsModal(true)} 
                className="bg-[#4F7CFF] hover:bg-[#4F7CFF]/90"
                data-testid="buy-more-credits-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Buy Credits
              </Button>
            </CardContent>
          </Card>

          {user?.plan === 'free' && (
            <Card className="border-dashed">
              <CardContent className="p-6 flex items-center gap-4">
                <AlertTriangle className="w-8 h-8 text-muted-foreground" />
                <div className="flex-1">
                  <p className="font-medium">Want monthly credits instead?</p>
                  <p className="text-sm text-muted-foreground">
                    Upgrade to Growth for 500 credits/month
                  </p>
                </div>
                <Button variant="outline" onClick={() => setActiveTab('billing')} data-testid="upgrade-from-credits-btn">
                  View Plans
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Purchase History */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Purchase History</CardTitle>
            </CardHeader>
            <CardContent>
              {purchaseHistoryLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-12 bg-muted animate-pulse rounded" />
                  ))}
                </div>
              ) : purchaseHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Coins className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No credit purchases yet</p>
                  <p className="text-sm">Your purchase history will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {purchaseHistory.map((purchase) => (
                    <div 
                      key={purchase.id} 
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border"
                      data-testid={`purchase-${purchase.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className="font-medium">
                            +{purchase.credits.toLocaleString()} credits
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(purchase.completed_at || purchase.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">${purchase.amount.toFixed(2)}</p>
                        <p className="text-xs text-green-600">Completed</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Share Analytics Tab */}
      {activeTab === 'analytics' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {analyticsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <div className="h-20 bg-muted animate-pulse rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : shareAnalytics ? (
            <>
              {/* Overview Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                        <Share2 className="w-6 h-6 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold">{shareAnalytics.total_shares}</p>
                        <p className="text-sm text-muted-foreground">Total Shares</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                        <Eye className="w-6 h-6 text-green-500" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold">{shareAnalytics.total_views}</p>
                        <p className="text-sm text-muted-foreground">Total Views</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                        <MousePointer className="w-6 h-6 text-purple-500" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold">{shareAnalytics.total_clicks}</p>
                        <p className="text-sm text-muted-foreground">Social Clicks</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Platform Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Platform Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(shareAnalytics.platform_breakdown || {}).length > 0 ? (
                    <div className="space-y-4">
                      {Object.entries(shareAnalytics.platform_breakdown).map(([platform, clicks]) => {
                        const total = shareAnalytics.total_clicks || 1;
                        const percentage = Math.round((clicks / total) * 100);
                        
                        return (
                          <div key={platform} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white ${platformColors[platform] || 'bg-gray-500'}`}>
                                  {platformIcons[platform] || <ExternalLink className="w-4 h-4" />}
                                </div>
                                <span className="font-medium capitalize">{platform}</span>
                              </div>
                              <span className="text-sm text-muted-foreground">{clicks} clicks ({percentage}%)</span>
                            </div>
                            <Progress value={percentage} className="h-2" />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      No social clicks yet. Share your creations to see analytics!
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Top Performing Shares */}
              {shareAnalytics.top_shares?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <TrendingUp className="w-5 h-5" />
                      Top Performing Shares
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {shareAnalytics.top_shares.map((share, index) => (
                        <div key={share.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold text-muted-foreground">#{index + 1}</span>
                            <div>
                              <p className="font-medium">{share.title}</p>
                              <p className="text-xs text-muted-foreground capitalize">{share.type?.replace('_', ' ')}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-sm">
                            <Eye className="w-4 h-4 text-muted-foreground" />
                            <span>{share.views} views</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Share2 className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  No share data yet
                </h3>
                <p className="text-sm text-muted-foreground">
                  Start sharing your creations to see analytics here
                </p>
              </CardContent>
            </Card>
          )}
        </motion.div>
      )}

      {/* Admin Tab - Only visible to admin users */}
      {activeTab === 'admin' && isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Shield className="w-5 h-5 text-primary" />
                  Pending Testimonials
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={fetchPendingTestimonials}
                  disabled={adminLoading}
                  data-testid="refresh-testimonials-btn"
                >
                  {adminLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {adminLoading && pendingTestimonials.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : pendingTestimonials.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">No pending testimonials to review</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingTestimonials.map((testimonial) => (
                    <div 
                      key={testimonial.id}
                      className="p-4 bg-muted/30 rounded-xl border border-border"
                      data-testid={`testimonial-${testimonial.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-4 h-4 ${star <= testimonial.rating 
                                    ? 'fill-amber-400 text-amber-400' 
                                    : 'text-muted-foreground'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {testimonial.user_email}
                            </span>
                          </div>
                          <p className="text-sm leading-relaxed">"{testimonial.text}"</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Submitted: {new Date(testimonial.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => handleApproveTestimonial(testimonial.id, false)}
                            disabled={processingId === testimonial.id}
                            data-testid={`reject-${testimonial.id}`}
                          >
                            {processingId === testimonial.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <XCircle className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApproveTestimonial(testimonial.id, true)}
                            disabled={processingId === testimonial.id}
                            data-testid={`approve-${testimonial.id}`}
                          >
                            {processingId === testimonial.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Buy Credits Modal */}
      <BuyCreditsModal 
        isOpen={showBuyCreditsModal} 
        onClose={() => setShowBuyCreditsModal(false)}
        onPurchaseComplete={() => {
          setShowBuyCreditsModal(false);
          refreshUser();
        }}
      />
    </div>
  );
};

export default SettingsPage;
